"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Users, Wrench, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
interface LocalizedText {
  vi: string;
  en: string;
}
interface MechanicItem {
  id: string;
  iconName: string;
  title: LocalizedText;
  description: LocalizedText;
  spec: LocalizedText;
}
const copy = {
  specRail: {
    vi: "Bản thiết kế vận hành // Cấu trúc phòng Lab",
    en: "Operational Blueprint // Lab Structure"
  },
  heading: {
    vi: "Mô hình lớp học thiết kế chuẩn mực cho tinh thần khám phá đích thực",
    en: "Classroom mechanics built for genuine discovery"
  },
  subtitle: {
    vi: "Quy mô nhóm nhỏ, trang thiết bị chuyên biệt và phản hồi sát sao mỗi tuần. Khám phá trải nghiệm thực tế khi con bước vào không gian phòng lab của học viện.",
    en: "Small groups, dedicated hardware, and immediate feedback every single week. Here is exactly what happens when your child steps into the academy laboratory."
  },
  imageBadge: {
    vi: "Lab Pod 04 — Chế tạo thực chiến",
    en: "Lab Pod 04 — Active Build"
  },
  imageAlt: {
    vi: "Giảng viên đồng hành cùng hai học viên kiểm thử mô hình xe Robotics tự chế tạo trong phòng lab",
    en: "Instructor kneeling beside two students testing their custom-built robotics vehicle in the lab"
  },
  cultureBadgeHeader: {
    vi: "Tiêu chuẩn thiết bị",
    en: "Equipment Standard"
  },
  cultureBadgePill: {
    vi: "Máy tính độc lập 1:1",
    en: "1:1 Computer Access"
  },
  cultureTitle: {
    vi: "Không dùng chung bàn phím. Không phải chờ đợi linh kiện.",
    en: "No sharing keyboards. No waiting for parts."
  },
  cultureDescription: {
    vi: "Mỗi bàn học được trang bị bộ giáo cụ Engineering tiêu chuẩn công nghiệp, vi điều khiển chuyên dụng và hai màn hình lập trình giúp học viên kiểm thử tư duy Coding theo thời gian thực.",
    en: "Every workstation is equipped with an industrial-grade engineering kit, individual microcontrollers, and dual programming screens so each student tests their own logic in real time."
  },
  mechanicsSectionTitle: {
    vi: "Chu trình học tập định kỳ rèn luyện tư duy độc lập tự chủ",
    en: "How our weekly cycles foster true independence"
  },
  mechanicsSectionDescription: {
    vi: "Thay vì ghi nhớ cú pháp hay lắp ráp theo mẫu sẵn, học viên tự mình tiếp cận quy trình xử lý sự cố khoa học. Khi đoạn mã Coding gặp lỗi hay cổng động cơ chưa phản hồi, giảng viên sẽ định hướng các em khoanh vùng nguyên nhân theo hệ thống, biến lỗi kỹ thuật thành trực giác cơ học và tư duy Robotics vững chắc.",
    en: "Rather than memorizing syntax or following static instructions, our students apply genuine scientific troubleshooting. When a line of code or a motor connection fails, mentors prompt students to isolate the error systematically, transforming technical mistakes into lasting mechanical intuition."
  },
  ctaAssessment: {
    vi: "Đăng ký đánh giá năng lực miễn phí",
    en: "Book a free assessment"
  },
  ctaCurriculum: {
    vi: "Xem lộ trình đào tạo chi tiết",
    en: "View full curriculum roadmap"
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png",
  alt: "Roboto academy mascot"
};
const mechanics: MechanicItem[] = [{
  id: "duration",
  iconName: "Clock",
  title: {
    vi: "Thời lượng 90 phút mỗi buổi",
    en: "90-minute weekly sessions"
  },
  description: {
    vi: "Khoảng thời gian lý tưởng để học viên tập trung đặt giả thuyết, chế tạo lắp ráp, đấu nối mạch và gỡ lỗi hệ thống tự động hóa phức tạp mà không bị gò bó.",
    en: "Sufficient continuous laboratory focus to hypothesize, construct, wire, and debug complex automated assemblies without rushed cleanups."
  },
  spec: {
    vi: "Thời lượng: 90 phút / buổi",
    en: "Session duration: 90 mins"
  }
}, {
  id: "ratio",
  iconName: "Users",
  title: {
    vi: "Giảng viên đồng hành trực tiếp",
    en: "Direct instructor guidance"
  },
  description: {
    vi: "Giảng viên đồng hành trực tiếp bên từng cụm thực hành, hướng dẫn tư duy thuật toán và logic thay vì giảng bài thụ động trên bảng đen.",
    en: "Instructors spend direct time alongside each workstation, guiding computational logic rather than broadcasting from a whiteboard."
  },
  spec: {
    vi: "Hình thức: Kèm cặp trực tiếp",
    en: "Format: Direct mentorship"
  }
}, {
  id: "hands-on",
  iconName: "Wrench",
  title: {
    vi: "Thời lượng thực hành trực tiếp xuyên suốt bài học",
    en: "Hands-on build time every lesson"
  },
  description: {
    vi: "Học viên dành hơn 75% thời lượng buổi học làm việc trực tiếp với vi điều khiển, động cơ servo, bánh răng truyền động và kiểm thử trực tiếp dòng lệnh Coding.",
    en: "Students spend over 75% of class working directly with microcontrollers, servo motors, mechanical gears, and live code."
  },
  spec: {
    vi: "Thực hành Engineering: 75%+",
    en: "Active engineering: 75%+"
  }
}, {
  id: "tracking",
  iconName: "BarChart3",
  title: {
    vi: "Cập nhật tiến độ học tập liên tục",
    en: "Continuous milestone tracking"
  },
  description: {
    vi: "Phụ huynh nhận báo cáo dự án điện tử sau mỗi buổi học, ghi nhận chi tiết các cột mốc thuật toán đã hoàn thành, thử thách Coding đã chinh phục và mô hình thực tế đã nghiệm thu.",
    en: "Parents receive digital project logs after each session detailing algorithmic milestones completed, code challenges conquered, and physical builds tested."
  },
  spec: {
    vi: "Báo cáo phụ huynh: Định kỳ hàng tuần",
    en: "Parent updates: Weekly portal"
  }
}];
export default function Classroom() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage access restrictions if any
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        if (e.newValue === "en" || e.newValue === "vi") {
          setLang(e.newValue);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const getIcon = (name: string) => {
    switch (name) {
      case "Clock":
        return <Clock className="h-5 w-5 text-secondary" />;
      case "Users":
        return <Users className="h-5 w-5 text-secondary" />;
      case "Wrench":
        return <Wrench className="h-5 w-5 text-secondary" />;
      case "BarChart3":
        return <BarChart3 className="h-5 w-5 text-secondary" />;
      default:
        return <CheckCircle2 className="h-5 w-5 text-secondary" />;
    }
  };
  return <section id="how-classes-work" data-nav="dark" className="relative overflow-hidden bg-foreground py-16 md:py-24 text-background">
      {/* Precision Blueprint Coordinate Line */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-background/15" />

      <div className="relative mx-auto max-w-[1440px] px-6 sm:px-8 md:px-12 xl:px-16">
        {/* Static Decorative Floating Mascot */}
        <div aria-hidden="true" className="hidden md:block absolute top-2 right-6 sm:right-8 md:right-12 xl:right-16 z-10 pointer-events-none select-none">
          <img src={defaultMascot.src} alt={defaultMascot.alt} className="h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
        </div>

        {/* Lab Spec Rail Motif */}
        <div className="flex items-center gap-3 mb-8">
          <div className="h-2 w-2 bg-secondary" />
          <div className="h-px w-16 bg-secondary" />
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-background/60">
            {copy.specRail[lang]}
          </span>
        </div>

        {/* Section Heading & Subtitle */}
        <div className="mb-12 max-w-3xl">
          <h2 className="font-serif text-3xl font-bold tracking-tight text-background sm:text-4xl lg:text-5xl">
            {copy.heading[lang]}
          </h2>
          <p className="mt-4 font-sans text-base text-background/70 sm:text-lg">
            {copy.subtitle[lang]}
          </p>
        </div>

        {/* Split Content Grid */}
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Left Column: Authentic Lab Photography & Hardware Spec Plate */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 flex flex-col gap-6">
            <div className="relative overflow-hidden rounded-xl border border-background/15 bg-background/5 p-2 shadow-sm">
              <AspectRatio ratio={4 / 3} className="overflow-hidden rounded-lg bg-background/10">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_instructor_small_group_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_instructor_small_group_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_instructor_small_group_lab.png-wvc-sizes" alt={copy.imageAlt[lang]} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
              </AspectRatio>

              {/* Technical Overlay Tag */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-lg border border-secondary/40 bg-foreground/90 px-3 py-1 backdrop-blur-xs">
                <span className="h-1.5 w-1.5 rounded-lg bg-secondary" />
                <span className="font-mono text-xs font-medium text-background">
                  {copy.imageBadge[lang]}
                </span>
              </div>
            </div>

            {/* Supporting Lab Culture Card */}
            <Card className="border border-background/15 bg-background/5 p-6 shadow-xs">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase text-background/60">
                    {copy.cultureBadgeHeader[lang]}
                  </span>
                  <span className="rounded-md bg-background/10 border border-background/15 px-2 py-0.5 font-mono text-xs text-background font-medium">
                    {copy.cultureBadgePill[lang]}
                  </span>
                </div>
                <h4 className="font-serif text-lg font-semibold text-background">
                  {copy.cultureTitle[lang]}
                </h4>
                <p className="font-sans text-sm text-background/70 leading-relaxed">
                  {copy.cultureDescription[lang]}
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Right Column: Comprehensive Narrative & 4 Core Mechanics */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: 0.05,
            ease: [0.16, 1, 0.3, 1]
          }} className="space-y-4">
              <h3 className="font-serif text-2xl font-bold tracking-tight text-background">
                {copy.mechanicsSectionTitle[lang]}
              </h3>
              <p className="font-sans text-base text-background/70 leading-relaxed">
                {copy.mechanicsSectionDescription[lang]}
              </p>
            </motion.div>

            {/* Mechanics Stack */}
            <div className="space-y-4">
              {mechanics.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              duration: 0.3,
              delay: 0.08 * index,
              ease: [0.16, 1, 0.3, 1]
            }}>
                  <Card className="group border border-background/15 bg-background/5 p-5 transition-[border-color,box-shadow] duration-200 hover:border-secondary/40 hover:shadow-xs">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary/15 border border-secondary/30">
                        {getIcon(item.iconName)}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h4 className="font-serif text-base font-semibold text-background">
                            {item.title[lang]}
                          </h4>
                          <span className="font-mono text-xs text-background/60">
                            {item.spec[lang]}
                          </span>
                        </div>
                        <p className="font-sans text-sm text-background/70 leading-relaxed">
                          {item.description[lang]}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>)}
            </div>

            {/* Conversion CTA Footer Block */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild className="bg-accent text-accent-foreground font-serif font-semibold shadow-xs hover:bg-accent/90">
                <Link to="/FreeTrialContactPage">
                  {copy.ctaAssessment[lang]}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-background/30 text-background bg-transparent hover:bg-background hover:text-foreground font-sans font-medium">
                <Link to="/LearningPathCurriculumPage">
                  {copy.ctaCurriculum[lang]}
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}